import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  Home,
  Car,
  User,
  BookOpen,
  Calculator,
  ArrowLeft,
  TrendingDown,
  IndianRupee,
  Clock,
  Percent,
  ChevronDown,
  RotateCcw,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./EmiCalculator.css";

const LOAN_TYPES = [
  { value: "home", label: "Home Loan", color: "#2563eb", Icon: Home },
  { value: "car", label: "Car Loan", color: "#0891b2", Icon: Car },
  { value: "personal", label: "Personal Loan", color: "#7c3aed", Icon: User },
  {
    value: "education",
    label: "Education Loan",
    color: "#10b981",
    Icon: BookOpen,
  },
];

const formatINR = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function EmiCalculator() {
  const [loanType, setLoanType] = useState("");
  const [amount, setAmount] = useState("");
  const [years, setYears] = useState("");
  const [interest, setInterest] = useState("");
  const [result, setResult] = useState(null);

  const activeType = LOAN_TYPES.find((t) => t.value === loanType);
  const accentColor = activeType?.color || "#2563eb";
  const accentEnd = activeType?.colorEnd || "#60a5fa";

  const reset = () => {
    setLoanType("");
    setAmount("");
    setYears("");
    setInterest("");
    setResult(null);
  };

  const calculate = () => {
    if (!amount || !years || !interest) {
      Swal.fire({
        title: "Missing Details",
        text: "Please fill in all fields to calculate your EMI.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: "#2563eb",
        width: 380,
      });
      return;
    }

    const P = Number(amount);
    const r = Number(interest) / 12 / 100;
    const n = Number(years) * 12;
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    const totalInterest = totalPayment - P;
    const principalPct = Math.round((P / totalPayment) * 100);
    const interestPct = 100 - principalPct;

    let balance = P;
    const breakdown = [];
    for (let y = 1; y <= Number(years); y++) {
      let yPrincipal = 0,
        yInterest = 0;
      for (let m = 0; m < 12; m++) {
        if (balance > 0) {
          const ip = balance * r;
          const pp = emi - ip;
          yInterest += ip;
          yPrincipal += pp;
          balance -= pp;
        }
      }
      breakdown.push({
        year: y,
        principal: Math.round(yPrincipal),
        interest: Math.round(yInterest),
        total: Math.round(yPrincipal + yInterest),
        balance: Math.round(Math.max(0, balance)),
      });
    }

    setResult({
      emi: Math.round(emi),
      total: Math.round(totalPayment),
      interest: Math.round(totalInterest),
      principalPct,
      interestPct,
      breakdown,
    });
  };

  return (
    <div className="ec-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="ec-hero">
        <div className="ec-hero-bg" />
        <div className="ec-hero-dots" />
        <div className="ec-hero-glow ec-glow-l" />
        <div className="ec-hero-glow ec-glow-r" />

        <div className="ec-hero-inner">
          <Link to="/calculators" className="ec-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="ec-hero-top">
            <div>
              <div className="ec-eyebrow">
                <Calculator size={12} /> EMI Calculators
              </div>
              <h1 className="ec-hero-title">
                Plan Your Loan
                <br />
                <span
                  className="ec-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  with Confidence
                </span>
              </h1>
              <p className="ec-hero-sub">
                Calculate monthly EMIs, total interest payable, and get a
                detailed year-by-year amortisation schedule.
              </p>
            </div>

            <div
              className="ec-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {activeType ? (
                <activeType.Icon size={36} color="#fff" />
              ) : (
                <Calculator size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Loan type chips */}
          <div className="ec-loan-chips">
            {LOAN_TYPES.map(({ value, label, color, Icon }) => (
              <button
                key={value}
                className={`ec-chip${loanType === value ? " ec-chip--active" : ""}`}
                style={
                  loanType === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => setLoanType(value)}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <div className="ec-content">
        {/* Input Card */}
        <div className="ec-card">
          <div
            className="ec-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, #0891b2)`,
            }}
          />
          <div className="ec-card-body">
            <div className="ec-section-title">
              Loan Details
              <span className="ec-section-line" />
            </div>

            <div className="ec-grid-2">
              {/* Loan Type */}
              <div className="ec-field">
                <label className="ec-label">
                  <TrendingDown size={12} /> Loan Type
                </label>
                <div className="ec-input-wrap">
                  <span className="ec-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <select
                    className="ec-select"
                    value={loanType}
                    onChange={(e) => setLoanType(e.target.value)}
                  >
                    <option value="" disabled>
                      Select Loan Type
                    </option>
                    {LOAN_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <span className="ec-select-arrow">
                    <ChevronDown size={14} />
                  </span>
                </div>
              </div>

              {/* Duration */}
              <div className="ec-field">
                <label className="ec-label">
                  <Clock size={12} /> Loan Duration (Years)
                </label>
                <div className="ec-input-wrap">
                  <span className="ec-input-icon">
                    <Clock size={14} />
                  </span>
                  <input
                    type="number"
                    className="ec-input"
                    placeholder="e.g., 20"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="ec-field">
                <label className="ec-label">
                  <IndianRupee size={12} /> Loan Amount (₹)
                </label>
                <div className="ec-input-wrap">
                  <span className="ec-input-icon">
                    <IndianRupee size={14} />
                  </span>
                  <input
                    type="number"
                    className="ec-input"
                    placeholder="e.g., 5000000"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              {/* Interest */}
              <div className="ec-field">
                <label className="ec-label">
                  <Percent size={12} /> Annual Interest Rate (%)
                </label>
                <div className="ec-input-wrap">
                  <span className="ec-input-icon">
                    <Percent size={14} />
                  </span>
                  <input
                    type="number"
                    className="ec-input"
                    placeholder="e.g., 8.5"
                    value={interest}
                    onChange={(e) => setInterest(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="ec-btn-row">
              <button
                className="ec-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, #0891b2)`,
                }}
                onClick={calculate}
              >
                <Calculator size={16} /> Calculate EMI
              </button>
              <button className="ec-reset-btn" onClick={reset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            {/* Summary + Bars */}
            <div className="ec-card">
              <div
                className="ec-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, #0891b2)`,
                }}
              />
              <div className="ec-card-body">
                <div className="ec-section-title">
                  EMI Summary
                  <span className="ec-section-line" />
                </div>

                <div className="ec-result-grid">
                  {[
                    {
                      label: "Monthly EMI",
                      value: result.emi,
                      color: "#2563eb",
                    },
                    {
                      label: "Principal Amount",
                      value: Number(amount),
                      color: "#0891b2",
                    },
                    {
                      label: "Total Interest",
                      value: result.interest,
                      color: "#ef4444",
                    },
                    {
                      label: "Total Payable",
                      value: result.total,
                      color: "#10b981",
                    },
                  ].map(({ label, value, color }) => (
                    <div
                      key={label}
                      className="ec-result-card"
                      style={{
                        background: `linear-gradient(135deg, ${color}08, ${color}16)`,
                        borderColor: `${color}28`,
                      }}
                    >
                      <div className="ec-result-label">{label}</div>
                      <div className="ec-result-value" style={{ color }}>
                        ₹ {formatINR(value)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Progress bars */}
                <div className="ec-section-title ec-section-title--spaced">
                  Payment Breakdown
                  <span className="ec-section-line" />
                </div>

                {[
                  {
                    label: "Principal",
                    pct: result.principalPct,
                    amt: Number(amount),
                    color: "#0891b2",
                  },
                  {
                    label: "Interest",
                    pct: result.interestPct,
                    amt: result.interest,
                    color: "#ef4444",
                  },
                ].map(({ label, pct, amt, color }) => (
                  <div key={label} className="ec-bar-group">
                    <div className="ec-bar-row">
                      <span className="ec-bar-label">
                        {label} ({pct}%)
                      </span>
                      <span className="ec-bar-amt">₹ {formatINR(amt)}</span>
                    </div>
                    <div className="ec-bar-track">
                      <div
                        className="ec-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${color}, ${color}88)`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amortisation Table */}
            <div className="ec-card">
              <div
                className="ec-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, #0891b2)`,
                }}
              />
              <div className="ec-card-body">
                <div className="ec-section-title">
                  Yearly Amortisation Schedule
                  <span className="ec-section-line" />
                </div>
                <div className="ec-table-wrap">
                  <table className="ec-table">
                    <thead>
                      <tr>
                        {[
                          "Year",
                          "Principal (₹)",
                          "Interest (₹)",
                          "Total Payment (₹)",
                          "Balance (₹)",
                        ].map((h) => (
                          <th key={h} className="ec-th">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.breakdown.map((row, i) => (
                        <tr
                          key={row.year}
                          className={i % 2 === 0 ? "ec-tr-even" : "ec-tr-odd"}
                        >
                          <td className="ec-td ec-td-year">Year {row.year}</td>
                          <td className="ec-td ec-td-mono">
                            {formatINR(row.principal)}
                          </td>
                          <td className="ec-td ec-td-mono">
                            {formatINR(row.interest)}
                          </td>
                          <td className="ec-td ec-td-mono">
                            {formatINR(row.total)}
                          </td>
                          <td
                            className={`ec-td ec-td-mono${row.balance === 0 ? " ec-td-paid" : ""}`}
                          >
                            {row.balance === 0
                              ? "✓ Paid Off"
                              : formatINR(row.balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        {/* CTA Banner */}
        <div className="ec-cta-banner">
          <div className="ec-cta-text">
            <h3 className="ec-cta-title">Need a personalised loan strategy?</h3>
            <p className="ec-cta-sub">
              Our advisors can help you choose the right loan and repayment plan
              for your goals.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_EMI)} className="ec-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
